import type { PhysicalIdentity } from "../types/physical"
export interface ImportContext {
  uuid: string
  label: string
  physicalComponentUuid: string | null
}
export interface ImportEquipment {
  uuid: string
  inspectionUuid: string
  inspectionName: string
  executionDate: string | null
  name: string
  reference: string
  manufacturer: string
  linkVersion: number
  physicalAssetUuid: string | null
  contexts: ImportContext[]
  matches: { uuid: string; reason: string; strong: boolean }[]
  suggestedGroup: string
}
export interface EquipmentImportPreview {
  token: string
  site: PhysicalIdentity
  equipment: ImportEquipment[]
  assets: PhysicalIdentity[]
  components: PhysicalIdentity[]
}
export interface NewImportAsset {
  key: string
  name: string
  code: string
  manufacturer: string
  model: string
  serialNumber: string
  notes: string
}
export interface NewImportComponent {
  key: string
  name: string
  code: string
  notes: string
}
export interface EquipmentImportWrite {
  previewToken: string
  confirmed: boolean
  reason: string
  equipment: {
    equipmentUuid: string
    assetUuid?: string
    assetKey?: string
    components: { contextUuid: string; componentUuid?: string; componentKey?: string }[]
  }[]
  newAssets: NewImportAsset[]
  newComponents: NewImportComponent[]
}
export interface EquipmentImportResult {
  assetsCreated: number
  componentsCreated: number
  equipmentLinked: number
  componentsLinked: number
}
export interface EquipmentImportDraft {
  targets: Record<string, string>
  components: Record<string, string>
  assets: Record<string, NewImportAsset>
  newComponents: Record<string, NewImportComponent>
}
const norm = (s: string) => s.toLocaleLowerCase().trim().replace(/\s+/g, " ")
export function initialImportDraft(preview: EquipmentImportPreview): EquipmentImportDraft {
  const draft: EquipmentImportDraft = { targets: {}, components: {}, assets: {}, newComponents: {} }
  for (const e of preview.equipment) {
    draft.targets[e.uuid] = e.physicalAssetUuid ? `asset:${e.physicalAssetUuid}` : ""
    draft.assets[e.uuid] = {
      key: e.uuid,
      name: e.name.trim().slice(0, 250) || "Équipement",
      code: "",
      manufacturer: e.manufacturer.slice(0, 250),
      model: e.reference.slice(0, 250),
      serialNumber: "",
      notes: "",
    }
    for (const c of e.contexts) {
      draft.components[c.uuid] = c.physicalComponentUuid
        ? `component:${c.physicalComponentUuid}`
        : ""
      draft.newComponents[c.uuid] = {
        key: c.uuid,
        name: c.label.slice(0, 250),
        code: "",
        notes: "",
      }
    }
  }
  return draft
}
export function proposeImportEquipment(
  preview: EquipmentImportPreview,
  draft: EquipmentImportDraft,
): EquipmentImportDraft {
  const next = { ...draft, targets: { ...draft.targets }, components: { ...draft.components } }
  const claims = new Set(
    preview.equipment
      .filter((e) => e.physicalAssetUuid)
      .map((e) => `${e.inspectionUuid}/asset:${e.physicalAssetUuid}`),
  )
  for (const e of preview.equipment) {
    if (e.physicalAssetUuid) continue
    const matches = e.matches.filter(
      (m) => m.strong && !claims.has(`${e.inspectionUuid}/asset:${m.uuid}`),
    )
    // Multiple matches are left for manual review. A reference can denote a model shared by many assets.
    const target =
      matches.length === 1
        ? `asset:${matches[0]!.uuid}`
        : matches.length > 1
          ? ""
          : `new:${e.suggestedGroup || e.uuid}`
    next.targets[e.uuid] = target
    for (const c of e.contexts) if (!c.physicalComponentUuid) next.components[c.uuid] = ""
    if (target) claims.add(`${e.inspectionUuid}/${target}`)
  }
  return next
}
export function proposeImportComponents(
  preview: EquipmentImportPreview,
  draft: EquipmentImportDraft,
): EquipmentImportDraft {
  const next = { ...draft, components: { ...draft.components } }
  const groups = new Map<string, string>(),
    counts = new Map<string, number>()
  for (const e of preview.equipment)
    for (const c of e.contexts) {
      const key = `${e.uuid}/${norm(c.label)}`
      counts.set(key, (counts.get(key) || 0) + 1)
    }
  for (const e of preview.equipment) {
    const parent = draft.targets[e.uuid]
    if (!parent) continue
    const used = new Set(
      e.contexts
        .filter((c) => c.physicalComponentUuid)
        .map((c) => `component:${c.physicalComponentUuid}`),
    )
    for (const c of e.contexts) {
      if (c.physicalComponentUuid) continue
      const matches = parent.startsWith("asset:")
        ? preview.components.filter(
            (x) =>
              !x.archived &&
              x.physicalAssetUuid === parent.slice(6) &&
              norm(x.name) === norm(c.label),
          )
        : []
      let target = ""
      if (matches.length === 1 && !used.has(`component:${matches[0]!.uuid}`))
        target = `component:${matches[0]!.uuid}`
      else if (matches.length === 0) {
        const key = `${parent}/${norm(c.label)}`
        const unique = counts.get(`${e.uuid}/${norm(c.label)}`) === 1
        const group = unique ? groups.get(key) || c.uuid : c.uuid
        if (unique) groups.set(key, group)
        target = `new:${group}`
      }
      next.components[c.uuid] = target
      if (target) used.add(target)
    }
  }
  return next
}
export function buildEquipmentImport(
  preview: EquipmentImportPreview,
  draft: EquipmentImportDraft,
  reason: string,
  confirmed: boolean,
): EquipmentImportWrite {
  const equipment: EquipmentImportWrite["equipment"] = [],
    assetKeys = new Set<string>(),
    componentKeys = new Set<string>()
  for (const e of preview.equipment) {
    const target = draft.targets[e.uuid]
    if (!target) continue
    const components = e.contexts
      .filter((c) => !c.physicalComponentUuid && !!draft.components[c.uuid])
      .map((c) => {
        const value = draft.components[c.uuid]!
        if (value.startsWith("new:")) {
          componentKeys.add(value.slice(4))
          return { contextUuid: c.uuid, componentKey: value.slice(4) }
        }
        return { contextUuid: c.uuid, componentUuid: value.slice(10) }
      })
    if (e.physicalAssetUuid && !components.length) continue
    if (target.startsWith("new:")) {
      assetKeys.add(target.slice(4))
      equipment.push({ equipmentUuid: e.uuid, assetKey: target.slice(4), components })
    } else equipment.push({ equipmentUuid: e.uuid, assetUuid: target.slice(6), components })
  }
  return {
    previewToken: preview.token,
    confirmed,
    reason: reason.trim(),
    equipment,
    newAssets: [...assetKeys].map((key) => ({ ...draft.assets[key]! })),
    newComponents: [...componentKeys].map((key) => ({ ...draft.newComponents[key]! })),
  }
}
export function validateEquipmentImport(
  preview: EquipmentImportPreview,
  draft: EquipmentImportDraft,
): string[] {
  const errors: string[] = [],
    assets = new Set<string>(),
    parents = new Map<string, string>()
  for (const e of preview.equipment) {
    const target = draft.targets[e.uuid]
    if (!target) continue
    const key = `${e.inspectionUuid}/${target}`
    if (assets.has(key))
      errors.push("Deux équipements d’une même inspection désignent le même équipement physique.")
    assets.add(key)
    const used = new Set<string>()
    for (const c of e.contexts) {
      const value = draft.components[c.uuid]
      if (!value) continue
      if (used.has(value))
        errors.push("Deux contextes du même équipement désignent le même composant physique.")
      used.add(value)
      if (value.startsWith("new:")) {
        if (parents.has(value) && parents.get(value) !== target)
          errors.push("Un composant proposé est affecté à plusieurs équipements physiques.")
        parents.set(value, target)
      }
    }
  }
  const plan = buildEquipmentImport(preview, draft, "", false)
  if (
    plan.newAssets.some((a) => !a.name?.trim()) ||
    plan.newComponents.some((c) => !c.name?.trim())
  )
    errors.push("Chaque nouvel équipement ou composant doit avoir un nom.")
  return [...new Set(errors)]
}
